import {
  canonicalRoleKey,
  type CreateResourceRoleRequest,
  type ResourceRoleCategoryDescription,
  type ResourceRoleSummary,
  type ResourceRolesListResponse,
  type UpdateResourceRoleRequest,
} from '@protoss/shared';
import { Button, Collapsible, NumberInput, SkeletonBlock, TextArea, TextField } from '@protoss/ui';
import * as React from 'react';
import { ConfirmDeleteDialog } from '../../components/ConfirmDeleteDialog';
import { ResizableDivider } from '../../components/ResizableDivider';
import { apiFetch } from '../../lib/apiClient';
import { labelize } from './labels';
import './resource-roles.css';

/** Ask the panel to open one role's description for a category (from the staffing grid). */
export interface RoleDescriptionFocus {
  roleKey: string;
  categoryKey: string | null;
  /** Bump to re-apply the same focus. */
  token: number;
}

/** Sentinel selection for the "add a new role" form (vs. an existing role uuid). */
const NEW_ROLE = '__new__';

interface CategoryText {
  userJobDescription: string;
  aiFunctionalDescription: string;
}

interface RoleFormState {
  roleName: string;
  level: string;
  category: string;
  onshore: string;
  offshore: string;
  currency: string;
  userJobDescription: string;
  aiFunctionalDescription: string;
  /** categoryKey -> override text; '' in both = uses the default. */
  categories: Record<string, CategoryText>;
}

const EMPTY_FORM: RoleFormState = {
  roleName: '',
  level: '',
  category: '',
  onshore: '0',
  offshore: '0',
  currency: 'USD',
  userJobDescription: '',
  aiFunctionalDescription: '',
  categories: {},
};

function categoryTextOf(role: ResourceRoleSummary, categoryKey: string): CategoryText {
  const o = role.categoryDescriptions.find((d) => d.categoryKey === categoryKey);
  return {
    userJobDescription: o?.userJobDescription ?? '',
    aiFunctionalDescription: o?.aiFunctionalDescription ?? '',
  };
}

const hasText = (t: CategoryText): boolean =>
  !!(t.userJobDescription.trim() || t.aiFunctionalDescription.trim());

/** The overrides that changed vs. the saved role (both empty = remove the override). */
function changedCategories(
  role: ResourceRoleSummary,
  categoryKeys: string[],
  form: RoleFormState
): ResourceRoleCategoryDescription[] {
  return categoryKeys.flatMap((categoryKey) => {
    const saved = categoryTextOf(role, categoryKey);
    const next = form.categories[categoryKey] ?? saved;
    const same =
      saved.userJobDescription.trim() === next.userJobDescription.trim() &&
      saved.aiFunctionalDescription.trim() === next.aiFunctionalDescription.trim();
    if (same) return [];
    return [
      {
        categoryKey,
        userJobDescription: next.userJobDescription.trim() || null,
        aiFunctionalDescription: next.aiFunctionalDescription.trim() || null,
      },
    ];
  });
}

function formFromRole(role: ResourceRoleSummary, categoryKeys: string[]): RoleFormState {
  return {
    roleName: role.roleName,
    level: role.level ?? '',
    category: role.category ?? '',
    onshore: String(role.onshore),
    offshore: String(role.offshore),
    currency: role.currency,
    userJobDescription: role.userJobDescription ?? '',
    aiFunctionalDescription: role.aiFunctionalDescription ?? '',
    categories: Object.fromEntries(categoryKeys.map((k) => [k, categoryTextOf(role, k)])),
  };
}

/** Parse a NumberInput display value ("1,200") into a non-negative integer. */
function toRate(value: string): number {
  const n = parseInt(value.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function rateSummary(role: ResourceRoleSummary): string {
  return `${role.currency} ${role.onshore.toLocaleString('en-US')} on / ${role.offshore.toLocaleString('en-US')} off`;
}

/**
 * ResourceRolesPanel - master-detail CRUD for the workspace-global, per-product `resource_role`
 * catalog (the source of truth read by the estimate runtime). Reusable: render it with any
 * `productKey` (the workspace Resources tab supplies a product selector; a product admin page can
 * embed it directly). Editing rates here affects every deal category for the product.
 *
 * What a role DOES can differ per deal category (COS-TBD per-category-role-descriptions):
 * the default description applies everywhere, and each category can override it. An
 * override is also what puts a role on that category's AI-staffed (`ai` plan) roster.
 */
export function ResourceRolesPanel({
  productKey,
  onRolesChanged,
  focus,
}: {
  productKey: string;
  /** Called after a role is created, edited or removed (e.g. to refresh the staffing grid). */
  onRolesChanged?: () => void;
  /** Open this role's description for this category (e.g. from the staffing grid). */
  focus?: RoleDescriptionFocus | null;
}) {
  const baseUrl = `/api/workspace/admin/resource-roles?productKey=${encodeURIComponent(productKey)}`;

  const [roles, setRoles] = React.useState<ResourceRoleSummary[]>([]);
  const [categoryKeys, setCategoryKeys] = React.useState<string[]>([]);
  const [openCategories, setOpenCategories] = React.useState<Set<string>>(new Set());
  const [focusedCategory, setFocusedCategory] = React.useState<string | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const [selected, setSelected] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<RoleFormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const [navWidth, setNavWidth] = React.useState(220);

  const isNew = selected === NEW_ROLE;
  const selectedRole = React.useMemo(
    () => (isNew ? null : roles.find((r) => r.uuid === selected) ?? null),
    [roles, selected, isNew]
  );
  const panelOpen = selected !== null;

  // Load roles whenever the product changes.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSelected(null);

    apiFetch<ResourceRolesListResponse>(baseUrl)
      .then((res) => {
        if (cancelled) return;
        setRoles(res.roles);
        setCategoryKeys(res.categoryKeys ?? []);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load resource roles.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  function openRole(role: ResourceRoleSummary, categoryKey: string | null = null) {
    setSelected(role.uuid);
    setForm(formFromRole(role, categoryKeys));
    setSaveError(null);
    setOpenCategories(categoryKey ? new Set([categoryKey]) : new Set());
    setFocusedCategory(categoryKey);
  }

  // Open the role (and category) the staffing grid asked for, once the roles are loaded.
  React.useEffect(() => {
    if (!focus || loading) return;
    const want = canonicalRoleKey(focus.roleKey);
    const role = roles.find((r) => canonicalRoleKey(r.roleKey) === want);
    if (!role) return;
    openRole(role, focus.categoryKey && categoryKeys.includes(focus.categoryKey) ? focus.categoryKey : null);
    rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    // Re-run only for a new focus request or once loading finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.token, loading]);

  function toggleCategory(categoryKey: string, open: boolean) {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (open) next.add(categoryKey);
      else next.delete(categoryKey);
      return next;
    });
  }

  function setCategoryField(categoryKey: string, key: keyof CategoryText, value: string) {
    setForm((prev) => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryKey]: {
          ...(prev.categories[categoryKey] ?? { userJobDescription: '', aiFunctionalDescription: '' }),
          [key]: value,
        },
      },
    }));
  }

  function openNew() {
    setSelected(NEW_ROLE);
    setForm(EMPTY_FORM);
    setSaveError(null);
  }

  function closePanel() {
    setSelected(null);
    setSaveError(null);
  }

  function setField<K extends keyof RoleFormState>(key: K, value: RoleFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (!form.roleName.trim()) {
      setSaveError('Role name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        const payload: CreateResourceRoleRequest = {
          roleName: form.roleName.trim(),
          level: form.level.trim() || null,
          category: form.category.trim() || null,
          onshore: toRate(form.onshore),
          offshore: toRate(form.offshore),
          currency: form.currency.trim() || 'USD',
          userJobDescription: form.userJobDescription.trim() || null,
          aiFunctionalDescription: form.aiFunctionalDescription.trim() || null,
        };
        const res = await apiFetch<{ role: ResourceRoleSummary }>(baseUrl, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setRoles((prev) => [...prev, res.role]);
        openRole(res.role);
        onRolesChanged?.();
      } else if (selectedRole) {
        const payload: UpdateResourceRoleRequest = {
          roleName: form.roleName.trim(),
          level: form.level.trim() || null,
          category: form.category.trim() || null,
          onshore: toRate(form.onshore),
          offshore: toRate(form.offshore),
          currency: form.currency.trim() || 'USD',
          userJobDescription: form.userJobDescription.trim() || null,
          aiFunctionalDescription: form.aiFunctionalDescription.trim() || null,
        };
        const categoryDescriptions = changedCategories(selectedRole, categoryKeys, form);
        if (categoryDescriptions.length > 0) payload.categoryDescriptions = categoryDescriptions;
        const res = await apiFetch<{ role: ResourceRoleSummary }>(
          `/api/workspace/admin/resource-roles/${selectedRole.uuid}?productKey=${encodeURIComponent(productKey)}`,
          { method: 'PATCH', body: JSON.stringify(payload) }
        );
        setRoles((prev) => prev.map((r) => (r.uuid === res.role.uuid ? res.role : r)));
        setForm(formFromRole(res.role, categoryKeys));
        onRolesChanged?.();
      }
    } catch {
      setSaveError('Failed to save the role. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selectedRole) return;
    setDeleting(true);
    try {
      await apiFetch(
        `/api/workspace/admin/resource-roles/${selectedRole.uuid}?productKey=${encodeURIComponent(productKey)}`,
        { method: 'DELETE' }
      );
      setRoles((prev) => prev.filter((r) => r.uuid !== selectedRole.uuid));
      onRolesChanged?.();
      setConfirmingDelete(false);
      closePanel();
    } catch {
      setSaveError('Failed to delete the role. Please try again.');
    } finally {
      setDeleting(false);
    }
  }

  // ── Left nav: role list ─────────────────────────────────────────────────
  const nav = (
    <nav className="rr-nav" aria-label="Resource roles" style={{ width: `${navWidth}px` }}>
      {loading ? (
        <div className="rr-nav__skeletons">
          <SkeletonBlock height="36px" />
          <SkeletonBlock height="36px" />
          <SkeletonBlock height="36px" />
        </div>
      ) : loadError ? (
        <p className="rr-nav__error">{loadError}</p>
      ) : (
        <>
          {roles.map((role) => (
            <button
              key={role.uuid}
              type="button"
              className={`rr-nav__item${role.uuid === selected ? ' rr-nav__item--active' : ''}`}
              onClick={() => openRole(role)}
              aria-current={role.uuid === selected ? 'true' : undefined}
            >
              <span className="rr-nav__item-text">
                <span className="rr-nav__item-name">{role.roleName}</span>
                <span className="rr-nav__item-meta">
                  {[role.level, role.category].filter(Boolean).join(' · ') || rateSummary(role)}
                </span>
              </span>
            </button>
          ))}

          <button
            type="button"
            className={`rr-nav__add-btn${isNew ? ' rr-nav__add-btn--active' : ''}`}
            onClick={openNew}
            aria-label="Add role"
          >
            <span aria-hidden="true">+</span> Add role
          </button>
        </>
      )}
    </nav>
  );

  // ── Right detail: view / edit the selected role ─────────────────────────
  const detail = (
    <div className="rr-detail">
      {!panelOpen ? (
        <p className="rr-detail__empty">Select a role to view and edit it, or add a new one.</p>
      ) : (
        <>
          <div className="rr-detail__header">
            <h3 className="rr-detail__title">{isNew ? 'New role' : selectedRole?.roleName}</h3>
            {!isNew && selectedRole && (
              <span className="rr-detail__key">Key: {selectedRole.roleKey}</span>
            )}
          </div>

          <div className="rr-detail__body">
            <TextField
              label="Role name"
              value={form.roleName}
              onChange={(e) => setField('roleName', e.target.value)}
              required
              disabled={saving}
            />

            <div className="rr-detail__row">
              <TextField
                label="Level"
                placeholder="e.g. senior"
                value={form.level}
                onChange={(e) => setField('level', e.target.value)}
                disabled={saving}
              />
              <TextField
                label="Category"
                placeholder="e.g. delivery"
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="rr-detail__row">
              <NumberInput
                label="Onshore rate"
                value={form.onshore}
                min={0}
                onChange={(e) => setField('onshore', e.target.value)}
                disabled={saving}
              />
              <NumberInput
                label="Offshore rate"
                value={form.offshore}
                min={0}
                onChange={(e) => setField('offshore', e.target.value)}
                disabled={saving}
              />
              <TextField
                label="Currency"
                value={form.currency}
                onChange={(e) => setField('currency', e.target.value)}
                disabled={saving}
              />
            </div>
            <p className="rr-detail__note">
              Rates are workspace-global - editing affects every deal category for this product.
            </p>

            <h4 className="rr-detail__section-title">
              {categoryKeys.length > 0 ? 'Default description (all categories)' : 'Description'}
            </h4>
            <TextArea
              label="User job description"
              helperText="What this role does (human-entered)."
              rows={4}
              value={form.userJobDescription}
              onChange={(e) => setField('userJobDescription', e.target.value)}
              disabled={saving}
            />
            <TextArea
              label="AI functional description"
              helperText="Used by the resource-plan AI to infer this role's involvement."
              rows={4}
              value={form.aiFunctionalDescription}
              onChange={(e) => setField('aiFunctionalDescription', e.target.value)}
              disabled={saving}
            />

            {categoryKeys.length > 0 && (
              <>
                <h4 className="rr-detail__section-title">By deal category</h4>
                {isNew ? (
                  <p className="rr-detail__note">
                    Create the role first, then set what it does in each deal category.
                  </p>
                ) : (
                  <div className="rr-categories">
                    {categoryKeys.map((categoryKey) => {
                      const text = form.categories[categoryKey] ?? {
                        userJobDescription: '',
                        aiFunctionalDescription: '',
                      };
                      const overrides = hasText(text);
                      const label = labelize(categoryKey);
                      return (
                        <Collapsible.Root
                          key={categoryKey}
                          open={openCategories.has(categoryKey)}
                          onOpenChange={(open) => toggleCategory(categoryKey, open)}
                          className={
                            focusedCategory === categoryKey ? 'rr-category rr-category--focused' : 'rr-category'
                          }
                        >
                          <Collapsible.Trigger className="rr-category__trigger">
                            <span className="rr-category__chevron" aria-hidden="true">
                              ›
                            </span>
                            <span className="rr-category__name">{label}</span>
                            <span
                              className={
                                overrides
                                  ? 'rr-category__status rr-category__status--override'
                                  : 'rr-category__status'
                              }
                            >
                              {overrides ? 'Own description' : 'Uses default'}
                            </span>
                          </Collapsible.Trigger>
                          <Collapsible.Content className="rr-category__body">
                            <TextArea
                              label={`User job description (${label})`}
                              placeholder="Uses default"
                              rows={3}
                              value={text.userJobDescription}
                              onChange={(e) =>
                                setCategoryField(categoryKey, 'userJobDescription', e.target.value)
                              }
                              disabled={saving}
                            />
                            <TextArea
                              label={`AI functional description (${label})`}
                              placeholder="Uses default"
                              rows={3}
                              value={text.aiFunctionalDescription}
                              onChange={(e) =>
                                setCategoryField(categoryKey, 'aiFunctionalDescription', e.target.value)
                              }
                              disabled={saving}
                            />
                            <p className="rr-detail__note">
                              {overrides
                                ? `The AI reads this text for ${label} deals instead of the default, and this role is on the AI-staffed roster for ${label}.`
                                : `${label} deals use the default text. This role is on the AI-staffed roster for ${label} only if the resource plan lists it. Clear both fields to go back to the default.`}
                            </p>
                          </Collapsible.Content>
                        </Collapsible.Root>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {saveError && <p className="rr-detail__form-error">{saveError}</p>}
          </div>

          <div className="rr-detail__footer">
            {!isNew && selectedRole && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving}
              >
                Delete
              </Button>
            )}
            <div className="rr-detail__footer-right">
              <Button variant="ghost" size="sm" onClick={closePanel} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleSave()}
                disabled={saving || !form.roleName.trim()}
              >
                {saving ? 'Saving…' : isNew ? 'Create role' : 'Save changes'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="rr-master-detail" ref={rootRef}>
      {nav}
      <ResizableDivider
        direction="vertical"
        onResize={(delta) => setNavWidth((w) => Math.max(180, Math.min(360, w + delta)))}
        ariaLabel="Resize role list"
      />
      {detail}

      <ConfirmDeleteDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Delete role?"
        confirmKeyword={selectedRole?.roleName ?? ''}
        description="This removes the role from the catalog. Existing estimates already computed are unaffected."
        isPending={deleting}
        onConfirm={handleDelete}
      />
    </div>
  );
}
